import { useState, useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import PoweredBy from "../components/PoweredBy";
import {
  CheckCircle,
  Loader2,
  AlertCircle,
  Calendar,
  Clock,
  Users,
  Hourglass,
} from "lucide-react";
import LanguageSwitcher from "../components/LanguageSwitcher";
import { useT } from "../i18n";

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:4000/api";

interface ReservationData {
  confirmationCode: string;
  date: string;
  time: string;
  partySize: number;
  status: string;
  restaurantName: string;
}

export default function ConfirmReservation() {
  const { code } = useParams<{ code: string }>();
  // El email de confirmación ya trae `?t=` (token de notas/alergias). Esta
  // pantalla no pinta `notes`, así que no hace falta reenviarlo en las
  // llamadas de aquí — pero sí hay que propagarlo al enlace "Gestionar mi
  // reserva" para que la pantalla de gestión (`/r/:code`) siga viéndolas.
  const [searchParams] = useSearchParams();
  const notesToken = searchParams.get("t");
  const q = notesToken ? `?t=${encodeURIComponent(notesToken)}` : "";
  const { t, tPeople, formatDate: formatDateT, formatTime } = useT();
  const [loading, setLoading] = useState(true);
  const [reservation, setReservation] = useState<ReservationData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [alreadyConfirmed, setAlreadyConfirmed] = useState(false);
  const [awaitingRestaurant, setAwaitingRestaurant] = useState(false);

  useEffect(() => {
    const confirmReservation = async () => {
      try {
        const res = await fetch(
          `${API_URL}/public/reservations/${code}/confirm`,
          {
            method: "POST",
          },
        );

        const data = await res.json();

        if (!res.ok) {
          if (data.status === "double_confirmed") {
            setAlreadyConfirmed(true);
            // Get reservation details
            const detailsRes = await fetch(
              `${API_URL}/public/reservations/${code}`,
            );
            if (detailsRes.ok) {
              const detailsData = await detailsRes.json();
              setReservation(detailsData.reservation);
            }
          } else if (data.status === "awaiting_restaurant") {
            setAwaitingRestaurant(true);
            if (data.reservation) setReservation(data.reservation);
          } else if (data.status === "cancelled") {
            setError(t("confirmAlreadyCancelled"));
          } else {
            setError(data.message || t("confirmGenericErrorFallback"));
          }
          return;
        }

        // 202 Accepted (awaiting restaurant) is technically !ok in fetch terms,
        // but some browsers/proxies may pass it through differently — handle here too.
        if (data.status === "awaiting_restaurant") {
          setAwaitingRestaurant(true);
          if (data.reservation) setReservation(data.reservation);
          return;
        }

        setReservation(data.reservation);
      } catch (err) {
        setError(t("commonConnectionErrorLater"));
      } finally {
        setLoading(false);
      }
    };

    if (code) {
      confirmReservation();
    }
  }, [code]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-teal-600 mx-auto mb-4" />
          <p className="text-gray-600">{t("confirmLoading")}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100 p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
          <div className="flex justify-end mb-2">
            <LanguageSwitcher />
          </div>
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-800 mb-2">
            {t("confirmErrorHeading")}
          </h1>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  const formatDate = (dateString: string) => {
    try {
      return formatDateT(new Date(dateString), "full");
    } catch {
      return dateString;
    }
  };

  if (awaitingRestaurant) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-amber-50 to-yellow-100 p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full">
          <div className="flex justify-end mb-2">
            <LanguageSwitcher />
          </div>
          <div className="text-center mb-6">
            <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Hourglass className="w-12 h-12 text-amber-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-800 mb-2">
              {t("confirmAwaitingHeading")}
            </h1>
            <p className="text-gray-600">{t("confirmAwaitingBody")}</p>
          </div>

          {reservation && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 space-y-3">
              <h2 className="font-semibold text-amber-800 text-lg">
                {reservation.restaurantName}
              </h2>
              <div className="space-y-2 text-gray-700">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-amber-600" />
                  <span>{formatDate(reservation.date)}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-amber-600" />
                  <span>{formatTime(reservation.time)}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Users className="w-4 h-4 text-amber-600" />
                  <span>{tPeople(reservation.partySize)}</span>
                </div>
              </div>
              <div className="pt-2 border-t border-amber-200">
                <p className="text-sm text-amber-700">
                  <strong>{t("confirmCodeLabelInline")}</strong>{" "}
                  {reservation.confirmationCode}
                </p>
              </div>
            </div>
          )}

          <a
            href={`/r/${code}${q}`}
            className="mt-6 block text-center bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 font-medium py-3 px-4 rounded-xl"
          >
            {t("commonManageReservationLink")}
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-teal-50 to-emerald-100 p-4">
      <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full">
        <div className="flex justify-end mb-2">
          <LanguageSwitcher />
        </div>
        <div className="text-center mb-6">
          <div className="w-20 h-20 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-12 h-12 text-teal-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-800 mb-2">
            {alreadyConfirmed
              ? t("confirmAlreadyHeading")
              : t("confirmSuccessHeading")}
          </h1>
          <p className="text-gray-600">
            {alreadyConfirmed ? t("confirmAlreadyBody") : t("confirmSuccessBody")}
          </p>
        </div>

        {reservation && (
          <div className="bg-teal-50 border border-teal-200 rounded-lg p-4 space-y-3">
            <h2 className="font-semibold text-teal-800 text-lg">
              {reservation.restaurantName}
            </h2>
            <div className="space-y-2 text-gray-700">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-teal-600" />
                <span>{formatDate(reservation.date)}</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-teal-600" />
                <span>{formatTime(reservation.time)}</span>
              </div>
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-teal-600" />
                <span>{tPeople(reservation.partySize)}</span>
              </div>
            </div>
            <div className="pt-2 border-t border-teal-200">
              <p className="text-sm text-teal-700">
                <strong>{t("confirmCodeLabelInline")}</strong>{" "}
                {reservation.confirmationCode}
              </p>
            </div>
          </div>
        )}

        <p className="text-center text-sm text-gray-500 mt-6">
          {t("confirmFooterText")}
        </p>

        <PoweredBy
          origen="confirmar-reserva"
          etiqueta={t("commonPoweredBy")}
          className="mt-4"
        />
      </div>
    </div>
  );
}
